export class RequestUrlBuilder<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  private readonly baseUrl: string;
  private readonly params: T;
  private readonly toKeyCase: (key: string) => string;

  constructor(
    baseUrl: string,
    params?: T,
    toKeyCase: (key: string) => string = RequestUrlBuilder.camelToSnakeCase,
  ) {
    this.baseUrl = baseUrl;
    this.params = params ?? ({} as T);
    this.toKeyCase = toKeyCase;
  }

  static camelToSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  static snakeToCamelCase(str: string): string {
    return str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  }

  /**
   * オブジェクトのキーをスネークケースからキャメルケースに変換
   */
  static convertKeysToCamelCase<T extends Record<string, unknown>>(obj: T): T {
    const result = {} as T;
    for (const [key, value] of Object.entries(obj)) {
      // 値が空文字やnull、undefinedの場合は含めない
      if (value !== undefined && value !== null && value !== "") {
        const camelCaseKey = RequestUrlBuilder.snakeToCamelCase(key);
        (result as Record<string, unknown>)[camelCaseKey] = value;
      }
    }
    return result;
  }

  /**
   * オブジェクトのキーをキャメルケースからスネークケースに変換
   */
  static convertKeysToSnakeCase<T extends Record<string, unknown>>(
    obj: T,
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeCaseKey = RequestUrlBuilder.camelToSnakeCase(key);
      result[snakeCaseKey] = value;
    }
    return result;
  }

  private buildQuery(): string {
    const entries = Object.entries(this.params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(
        ([k, v]) =>
          [this.toKeyCase(k), String(v)] as const satisfies [string, string],
      );
    return entries.length > 0
      ? `?${new URLSearchParams(entries).toString()}`
      : "";
  }

  build(): string {
    return `${this.baseUrl}${this.buildQuery()}`;
  }
}
